import { Box, HStack, Text } from "@chakra-ui/react";
import { dataAttr } from "@chakra-ui/utils";
import React from "react";
import { useClickableElementProps } from "shared/components/clickable/ClickableElementProps";
import { DataTableCellLink } from "shared/components/table/data/DataTableCellLink";
import {
  DataTableColumnContext,
  DataTableContext,
} from "shared/components/table/data/DataTableContext";
import { RenderHeaderValueProps } from "shared/components/table/data/DataTableTypes";
import { DataTableCellActions } from "shared/components/table/data/internal/DataTableCellActions";
import { DataTableCellBadge } from "shared/components/table/data/internal/DataTableCellBadge";
import { DataTableColumnMenu } from "shared/components/table/data/internal/DataTableColumnMenu";
import { ColumnSortState } from "shared/components/table/data/internal/DataTableColumnSort";
import { DataTableColumnSortIndicator } from "shared/components/table/data/internal/DataTableColumnSortIndicator";
import { isDataTableSensitive } from "shared/components/table/data/internal/DataTableInternalUtils";
import { MaybernTableColumnFilterIndicator } from "shared/components/table/maybern/MaybernTableColumnFilter";
import { parseCurrency, symbolForCurrency } from "shared/utils/MoneyUtils";

export interface DataTableHeaderRendererProps {
  tableContext: DataTableContext;
  columnContext: DataTableColumnContext;
  columnSort?: string | null;
  onClickColumnSort?: () => void;
  isFilterActive?: boolean;
  onClickColumnFilter?: () => void;
}

export const DataTableHeaderRenderer = React.memo<
  React.PropsWithChildren<DataTableHeaderRendererProps>
>(
  ({
    tableContext,
    columnContext,
    onClickColumnSort,
    columnSort,
    isFilterActive,
    onClickColumnFilter,
    children,
  }) => {
    const renderProps: RenderHeaderValueProps = {
      type: "header",
      tableContext,
      columnContext,
    };
    const linkInfo = tableContext.getLinkInfoForCell?.(renderProps);
    const actionsInfo = tableContext.getActionsForColumn?.(renderProps);
    const { title, subtitle, badge } = columnContext.column;

    let header = (
      <HStack
        w="100%"
        spacing="8px"
        data-ls-disabled={dataAttr(isDataTableSensitive(columnContext))}
        data-private={dataAttr(isDataTableSensitive(columnContext))}
        className="mb-header-cell-comp-header"
      >
        <Box overflow="hidden" className="mb-header-cell-comp-header-wrapper">
          {title && (
            <Text
              className="mb-header-cell-comp-title"
              overflow="hidden"
              textOverflow="ellipsis"
              data-currency-symbol={
                columnContext.inferredData?.valueType === "money" &&
                !columnContext.column.columns?.length
                  ? symbolForCurrency(
                      parseCurrency(
                        columnContext.inferredData.currency || "USD",
                      ),
                    )
                  : columnContext.inferredData?.valueType === "percent"
                    ? "%"
                    : undefined
              }
            >
              {title}
            </Text>
          )}
          {subtitle && (
            <Text className="mb-header-cell-comp-subtitle">{subtitle}</Text>
          )}
        </Box>
        {children}
        {isFilterActive && (
          <MaybernTableColumnFilterIndicator onClick={onClickColumnFilter} />
        )}
      </HStack>
    );

    if (linkInfo) {
      header = (
        <DataTableCellLink
          isColumnHeader={true}
          tableContext={tableContext}
          columnContext={columnContext}
          linkInfo={linkInfo}
          pointerEvents="none"
        >
          {header}
        </DataTableCellLink>
      );
    }

    const actions = actionsInfo ? (
      <DataTableCellActions
        tableContext={tableContext}
        columnContext={columnContext}
        actions={actionsInfo}
        onButtonClick={tableContext.onButtonClick}
      />
    ) : null;

    const linkProps = useClickableElementProps(linkInfo);

    return (
      <HStack
        className="mb-header-cell-comp"
        __css={tableContext.stylesForCell?.(renderProps)}
        onClick={onClickColumnSort}
        {...linkProps}
      >
        <HStack className="mb-header-cell-comp-wrapper" overflow="hidden">
          {badge && <DataTableCellBadge badge={badge} />}
          {header}
          {columnSort && (
            <DataTableColumnSortIndicator
              state={columnSort as ColumnSortState}
            />
          )}
        </HStack>
        {actions}
        <DataTableColumnMenu columnContext={columnContext} />
      </HStack>
    );
  },
);
DataTableHeaderRenderer.displayName = "DataTableHeaderRenderer";
